import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Tab, Nav, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import ccmService from '../../services/ccmService';
import Assessments from './Assessments';
import './PHMHub.css';

const PHMHub = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [enrollment, setEnrollment] = useState(null);
  
  // Mock fetching enrollment details
  useEffect(() => {
    // In a real app we'd fetch the specific enrollment details.
    // Simulating API call
    setTimeout(() => {
      setEnrollment({
         id: enrollmentId,
         patient_name: 'John Doe',
         mrn: 'MRN-92834',
         status: 'Active',
         care_manager: 'Emily Chen, RN',
         risk_score: 'High',
         last_visit: '2023-10-01',
         timer_running: false
      });
      setLoading(false);
    }, 800);
  }, [enrollmentId]);

  const handleStartVirtualVisit = async () => {
    try {
      const res = await ccmService.startVirtualVisit(enrollmentId);
      if(res.success) {
        navigate(`/ccm/care-plans/${enrollmentId}?visitId=${res.data.virtual_visit_id}`);
      }
    } catch(err) {
      console.error(err);
      // Fallback for simple demo
      navigate(`/ccm/care-plans/${enrollmentId}?visitId=demo123`);
    }
  };

  if (loading) {
     return (
      <Container fluid className="phm-hub-container d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="primary" />
      </Container>
     )
  }

  return (
    <Container fluid className="phm-hub-container py-4">
      {/* Patient Header */}
      <Card className="phm-header-card mb-4 shadow-sm border-0">
        <Card.Body className="d-flex justify-content-between align-items-center p-4">
          <div className="d-flex align-items-center gap-4">
            <div className="patient-avatar">
              {enrollment.patient_name.split(' ').map(n=>n[0]).join('')}
            </div>
            <div>
              <h3 className="mb-1 fw-bold">{enrollment.patient_name} <Badge bg="success" className="ms-2 fs-6">CCM Active</Badge></h3>
              <div className="text-muted d-flex gap-3">
                <span><i className="bi bi-person-badge"></i> {enrollment.mrn}</span>
                <span><i className="bi bi-person-heart"></i> Care Manager: {enrollment.care_manager}</span>
              </div>
            </div>
          </div>
          
          <div className="d-flex gap-3">
            <Button variant="outline-primary" className="timer-btn">
               <i className="bi bi-stopwatch"></i> Start Timer
            </Button>
            <Button variant="primary" className="fw-bold premium-btn" onClick={handleStartVirtualVisit}>
               <i className="bi bi-camera-video-fill me-2"></i> Start Virtual Visit
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Main Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Row>
          <Col md={2} className="mb-4">
            <Nav variant="pills" className="flex-column phm-nav">
              <Nav.Item>
                <Nav.Link eventKey="overview"><i className="bi bi-grid-1x2-fill me-2"></i> Overview</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="assessments"><i className="bi bi-clipboard2-pulse-fill me-2"></i> HRA Assessments</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="careplans" onClick={() => navigate(`/ccm/care-plans/${enrollmentId}`)}>
                   <i className="bi bi-file-earmark-medical-fill me-2"></i> Care Plans
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="timelogs"><i className="bi bi-clock-history me-2"></i> Time Logs</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
          <Col md={10}>
            <Tab.Content className="phm-content">
              
              <Tab.Pane eventKey="overview">
                <Row className="g-4">
                  <Col md={8}>
                     <Card className="border-0 shadow-sm h-100">
                       <Card.Header className="bg-white border-0 pt-4 pb-0">
                         <h5 className="fw-bold">Current Snapshot</h5>
                       </Card.Header>
                       <Card.Body>
                         <p className="text-muted">Acuity/Risk Score: <Badge bg="danger">High (5.0)</Badge></p>
                         <p className="text-muted">Last Virtual Visit: {enrollment.last_visit}</p>
                         <p className="text-muted">Current Month Time Tracked: <strong>15 mins</strong> / 20 mins required</p>
                         <div className="progress mt-2" style={{height: '10px'}}>
                           <div className="progress-bar bg-info" role="progressbar" style={{width: '75%'}}></div>
                         </div>
                       </Card.Body>
                     </Card>
                  </Col>
                  <Col md={4}>
                     <Card className="border-0 shadow-sm bg-light h-100">
                       <Card.Body className="d-flex flex-column justify-content-center align-items-center text-center">
                          <i className="bi bi-exclamation-circle text-warning fs-1 mb-2"></i>
                          <h5>Assessment Due</h5>
                          <p className="text-muted small">The initial Health Risk Assessment is required to begin care planning.</p>
                          <Button variant="outline-dark" size="sm" onClick={()=>setActiveTab('assessments')}>Begin Assessment</Button>
                       </Card.Body>
                     </Card>
                  </Col>
                </Row>
              </Tab.Pane>

              <Tab.Pane eventKey="assessments">
                 {/* Mount the Assessments component passing the enrollment ID */}
                 <Assessments enrollmentId={enrollmentId} />
              </Tab.Pane>

            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
};

export default PHMHub;
